import { useMutation, useQuery } from '@apollo/client';
import { gql } from '__generated__/gql';
import { AccreditationStatusEnumType, InvestmentEntityTypeEnumType } from '__generated__/graphql';
import classNames from 'classnames';
import useParentRoute from 'hooks/useParentRoute';
import { FC, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import ErrorMessage from 'components/ErrorMessage';
import { FormInput, FormPanel } from 'components/FormPanel';

import LoadingIndicator from 'primitives/LoadingIndicator';

import { getEligibilityOptions } from '../../Accreditation/EligibilityCriteria';
import ExampleTemplate from '../utils/ExampleTemplate';
import MoreAccreditationDetailsDialogText from '../utils/MoreAccreditationDetailsDialogText';
import { getInvestingExperienceValue, getTargetAllocationValue } from '../utils/options';

const INVESTOR_APPLICATION_QUERY = gql(`
  query InvestorApplication($id: ID!) {
    investmentEntity(id: $id) {
      id
      type
      investorApplication {
        id
        accreditationStatus
        accreditationProofDocument {
          url
          name
        }
        accreditationEligibilityCriteria
        targetAllocation
        investingExperience
      }
    }
  }
`);

const UPDATE_INVESTOR_APPLICATION_MUTATION = gql(`
  mutation UpsertInvestorApplication(
    $investmentEntityId: ID!
    $accreditationStatus: AccreditationStatusEnumType!
    $accreditationProofDocument: Upload
    $accreditationEligibilityCriteria: [AccreditationEligibilityCriteriaEnumType!]
    $targetAllocation: String!
    $investingExperience: String!
  ) {
    upsertInvestorApplication(
      investmentEntityId: $investmentEntityId
      accreditationStatus: $accreditationStatus
      accreditationProofDocument: $accreditationProofDocument
      accreditationEligibilityCriteria: $accreditationEligibilityCriteria
      targetAllocation: $targetAllocation
      investingExperience: $investingExperience
    ) {
      id
      investorApplication {
        id
        accreditationStatus
        accreditationProofDocument {
          url
          name
        }
        accreditationEligibilityCriteria
        targetAllocation
        investingExperience
      }
    }
  }
`);

const InvestorApplication: FC = () => {
  const navigate = useNavigate();
  const parentRoute = useParentRoute();
  const [onFormChangeData, setOnFormChangeData] = useState<any>({});

  const { investmentEntityId } = useParams<{ investmentEntityId: string }>() as {
    investmentEntityId: string;
  };

  const { loading, error, data, refetch } = useQuery(INVESTOR_APPLICATION_QUERY, {
    variables: {
      id: investmentEntityId,
    },
  });

  const [upsertInvestorApplication, { loading: upsertLoading, error: upsertError }] = useMutation(
    UPDATE_INVESTOR_APPLICATION_MUTATION
  );

  if (loading) return <LoadingIndicator />;

  if (error || !data || !investmentEntityId)
    return <ErrorMessage error={error} refetch={refetch} />;

  const investorApplication = data.investmentEntity.investorApplication;
  const investmentEntityType = data.investmentEntity.type as InvestmentEntityTypeEnumType;
  const eligibilityOptions = getEligibilityOptions(investmentEntityType);

  const showIneligibilityMessage = () => {
    if (!!onFormChangeData.qualifiesForAccreditation) {
      return onFormChangeData.qualifiesForAccreditation === 'DO_NOT_QUALIFY';
    }

    return investorApplication?.accreditationStatus === AccreditationStatusEnumType.NotAccredited;
  };

  const showExampleTemplate = () => {
    if (!!onFormChangeData.currentlyAccredited) {
      return onFormChangeData.currentlyAccredited === 'YES';
    }

    return (
      investorApplication?.accreditationStatus ===
      AccreditationStatusEnumType.PendingAlindiaVerification
    );
  };

  return (
    <FormPanel
      loading={upsertLoading}
      error={upsertError}
      onSubmit={formData => {
        // if (formData.qualifiesForAccreditation === 'DO_NOT_QUALIFY') return;

        const accreditationStatus =
          formData.currentlyAccredited === 'YES'
            ? AccreditationStatusEnumType.PendingAlindiaVerification
            : formData.qualifiesForAccreditation === 'QUALIFY'
            ? AccreditationStatusEnumType.InitiateProcess
            : AccreditationStatusEnumType.NotAccredited;

        upsertInvestorApplication({
          variables: {
            investmentEntityId,
            accreditationStatus,
            accreditationProofDocument:
              accreditationStatus === AccreditationStatusEnumType.PendingAlindiaVerification &&
              investorApplication?.accreditationProofDocument !==
                formData.accreditationProofDocument
                ? formData.accreditationProofDocument
                : undefined,
            accreditationEligibilityCriteria: formData.accreditationEligibilityCriteria || [],
            targetAllocation: formData.targetAllocation,
            investingExperience: formData.investingExperience,
          },
        }).then(() => {
          navigate(`${parentRoute}/submit-for-review`);
        });
      }}
      onCancel={() => {
        navigate(`${parentRoute}/kyc`);
      }}
      submitButtonLabel="Save and continue"
      cancelButtonLabel="Back"
    >
      <div className={classNames('my-1 col-span-full')}>
        <div className="rounded-md bg-blue-50 px-4 py-4 border-l-4 border-blue-400">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800 font-medium mb-2">
                As per SEBI's revised framework, all new investors participating in Angel Funds are
                required to be accredited investors.
              </p>
              <MoreAccreditationDetailsDialogText />
            </div>
          </div>
        </div>
      </div>
      <FormInput
        fullWidth
        type="radio-group"
        fieldName="qualifiesForAccreditation"
        label="Do you qualify as per the accreditation criteria specified above?"
        defaultValue={
          !investorApplication
            ? ''
            : investorApplication?.accreditationStatus === AccreditationStatusEnumType.NotAccredited
            ? 'DO_NOT_QUALIFY'
            : 'QUALIFY'
        }
        options={[
          {
            value: 'DO_NOT_QUALIFY',
            label: 'No, I do not qualify',
          },
          {
            value: 'QUALIFY',
            label: 'Yes, I qualify',
          },
        ]}
        validators={{
          required: true,
        }}
        onChange={value => {
          setOnFormChangeData(value);
        }}
      />
      {/* <p
        className={classNames(
          'text-red-500 my-1 col-span-full text-sm',
          showIneligibilityMessage() ? 'block' : 'hidden'
        )}
      >
        We cannot process your investor application at this time. Please resume your application
        once you are eligible for accreditation.
      </p> */}
      <FormInput
        fullWidth
        type="radio-group"
        fieldName="currentlyAccredited"
        label="Do you currently have a valid Accreditation Certificate issued by a SEBI-approved KYC Registration Agency?"
        helperText="SEBI-approved KYC Registration Agencies are CDSL Ventures Limited (CVL) and NSDL Database Management Limited (NDML)"
        defaultValue={
          !investorApplication
            ? ''
            : investorApplication?.accreditationStatus ===
              AccreditationStatusEnumType.PendingAlindiaVerification
            ? 'YES'
            : 'NO'
        }
        options={[
          { value: 'NO', label: 'No' },
          { value: 'YES', label: 'Yes' },
        ]}
        conditionsToShow={{
          matches: [
            {
              field: 'qualifiesForAccreditation',
              condition: '===',
              value: 'QUALIFY',
            },
          ],
        }}
        validators={{
          required: true,
        }}
        onChange={value => {
          setOnFormChangeData(value);
        }}
      />
      <div className={classNames('my-2 col-span-full', showExampleTemplate() ? 'block' : 'hidden')}>
        <ExampleTemplate />
      </div>
      <FormInput
        fieldName="accreditationProofDocument"
        label="Accreditation Proof Document *"
        helperText="Please provide an Accredited Investor (AI) Certificate"
        type="upload"
        defaultValue={investorApplication?.accreditationProofDocument || ''}
        validators={{
          required: true,
        }}
        conditionsToShow={{
          matches: [
            {
              field: 'currentlyAccredited',
              condition: '===',
              value: 'YES',
            },
          ],
        }}
        onChange={value => {
          setOnFormChangeData(value);
        }}
        maxFileSize={10}
      />
      <FormInput
        fullWidth
        type="checkbox-group"
        fieldName="accreditationEligibilityCriteria"
        label="Which of the following criteria do you qualify for?"
        helperText="You can select multiple options if applicable"
        defaultValue={investorApplication?.accreditationEligibilityCriteria || ''}
        compact={true}
        options={eligibilityOptions}
        validators={{
          required: true,
        }}
        conditionsToShow={{
          matches: [
            {
              field: 'currentlyAccredited',
              condition: '===',
              value: 'NO',
            },
            {
              field: 'qualifiesForAccreditation',
              condition: '===',
              value: 'QUALIFY',
            },
          ],
        }}
        onChange={value => {
          setOnFormChangeData(value);
        }}
      />
      <FormInput
        fieldName="targetAllocation"
        label="How much capital do you plan to invest using Angellist India over the next twelve months? *"
        type="radio-group"
        defaultValue={getTargetAllocationValue(investorApplication?.targetAllocation) || ''}
        options={[
          { label: 'Less than 25 lakhs', value: 'lessThan25Lakhs' },
          { label: '25-50 lakhs', value: '25To50Lakhs' },
          { label: '50 lakhs - 1 crore', value: '50LakhsTo1Crore' },
          { label: '1 crore +', value: '1CrorePlus' },
        ]}
        validators={{
          required: true,
        }}
      />
      <FormInput
        fieldName="investingExperience"
        label="What is your experience investing in venture-backed tech startups or capital funds? *"
        type="radio-group"
        defaultValue={getInvestingExperienceValue(investorApplication?.investingExperience) || ''}
        options={[
          {
            label:
              'I have invested in startups or emerging or early-stage ventures directly/ indirectly',
            value: 'invested',
          },
          {
            label: 'I am a senior management professional with at least 10 years of experience',
            value: 'seniorManagement',
          },
          {
            label: 'I represent/ have represented a family office',
            value: 'familyOffice',
          },
          { label: 'I have experience as a serial entrepreneur', value: 'entrepreneur' },
        ]}
        validators={{
          required: true,
        }}
      />
    </FormPanel>
  );
};

export default InvestorApplication;
